import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import {
  DriveFile,
  GoogleDriveService,
} from 'src/app/services/google-drive.service';

import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-google-drive-modal',
    templateUrl: './google-drive-modal.component.html',
    styleUrls: ['./google-drive-modal.component.scss'],
    standalone: false
})
export class GoogleDriveModalComponent implements OnInit {
  @Input() isVisible: boolean = false;
  @Output() fileSelected = new EventEmitter<{
    file: File;
    base64: string;
    fileName: string;
  }>();
  @Output() modalClosed = new EventEmitter<void>();

  files: DriveFile[] = [];
  selectedFile: DriveFile | null = null;
  loading = false;
  downloading = false;
  signingIn = false;
  isSignedIn = false;

  constructor(
    private driveService: GoogleDriveService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.driveService.initializeGapi().catch(err => {
    console.error('Failed to initialize Google Drive API:', err);
  });
    this.driveService.isSignedIn$.subscribe((signedIn) => {
      this.isSignedIn = signedIn;
      if (signedIn && this.isVisible) {
        this.loadFiles();
      }
    });
  }

  // Add this method to handle modal visibility changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      // Modal is being opened
      if (this.isSignedIn) {
        this.loadFiles();
      }
    }
  }

  async signInToGoogleDrive() {
    this.signingIn = true;
    try {
      await this.driveService.signIn();
    } catch (error: any) {
      const silent = ['popup_closed', 'access_denied', 'user_cancel'];
      if (!silent.includes(error?.message)) {
        alert('Failed to sign in to Google Drive. Please try again.');
      }
    } finally {
      this.cdr.detectChanges();
      this.signingIn = false;
    }
  }

  signOut() {
    this.driveService.signOut();
    this.files = [];
    this.selectedFile = null;
  }

  async loadFiles() {
    this.loading = true;
    try {
      this.files = await this.driveService.listFiles();
    } catch (error) {
      // console.error('Error loading files:', error);
    }
    this.loading = false;
  }

  selectFile(file: DriveFile) {
    this.selectedFile = file;
  }

  async downloadAndUseFile() {
    if (!this.selectedFile) return;

    this.downloading = true;
    try {
      const { blob, base64 } = await this.driveService.downloadFile(
        this.selectedFile.id
      );

      // Debug logging (commented out)
      // console.log('Downloaded file details:', {
      //   name: this.selectedFile.name,
      //   mimeType: this.selectedFile.mimeType,
      //   blobSize: blob.size,
      //   base64Length: base64.length,
      //   base64Preview: base64.substring(0, 100),
      // });

      if (blob.size === 0) {
        throw new Error('Downloaded Blob is empty');
      }

      // Debug: Test Blob download (commented out)
      // const blobTestUrl = URL.createObjectURL(blob);
      // const blobLink = document.createElement('a');
      // blobLink.href = blobTestUrl;
      // blobLink.download = `${this.selectedFile.name}_blob_test.pdf`;
      // blobLink.style.display = 'none';
      // document.body.appendChild(blobLink);
      // blobLink.click();
      // document.body.removeChild(blobLink);
      // setTimeout(() => URL.revokeObjectURL(blobTestUrl), 100);

      // Create File object from blob
      const file = new File([blob], this.selectedFile.name, {
        type: this.selectedFile.mimeType,
      });

      // Debug: File object logging (commented out)
      // console.log('Created File object:', {
      //   fileSize: file.size,
      //   fileType: file.type,
      //   fileName: file.name,
      // });

      // Debug: Test File download (commented out)
      // const fileTestUrl = URL.createObjectURL(file);
      // const fileLink = document.createElement('a');
      // fileLink.href = fileTestUrl;
      // fileLink.download = `${this.selectedFile.name}_file_test.pdf`;
      // fileLink.style.display = 'none';
      // document.body.appendChild(fileLink);
      // fileLink.click();
      // document.body.removeChild(fileLink);
      // setTimeout(() => URL.revokeObjectURL(fileTestUrl), 100);

      this.fileSelected.emit({
        file: file,
        base64: base64,
        fileName: this.selectedFile.name,
      });

      //    this.messageService.add({
      //   severity: 'success',
      //   detail: `File "${this.selectedFile.name}" uploaded successfully from Google Drive`
      // });

      this.closeModal();
    } catch (error) {
      // Debug error logging (commented out)
      // console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
    this.downloading = false;
  }
  closeModal() {
    this.isVisible = false;
    this.selectedFile = null;
    this.signingIn = false;
    this.loading = false;
    this.downloading = false; 
    this.modalClosed.emit();
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  }
}
